import type { AxiosError } from "axios";

import axios from "axios";
import Cookies from "js-cookie";

import type { ApiError } from "@/lib/error-handler";
import type { AxiosCustomRequestConfig } from "@/types/axios";

import { ACCESS_TOKEN, REFRESH_TOKEN, SERVER_URL } from "@/constants";

const baseURL = `${SERVER_URL}/api/v1`;

export const api = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
});

function setAuthCookies(accessToken: string, refreshToken: string) {
  Cookies.set(ACCESS_TOKEN, accessToken, { expires: 1 });
  Cookies.set(REFRESH_TOKEN, refreshToken, { expires: 7 });
}

api.interceptors.request.use(async (config) => {
  const token = Cookies.get(ACCESS_TOKEN);

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function logoutUser() {
  Cookies.remove(ACCESS_TOKEN);
  Cookies.remove(REFRESH_TOKEN);
  window.location.href = "/login";
}

let isRefreshing = false;
let failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason?: any) => void }> = [];

function processQueue(error: Error | null, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    }
    else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

api.interceptors.response.use(
  res => res,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as AxiosCustomRequestConfig;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${baseURL}/auth/refresh-token`, {}, {
          withCredentials: true,
        });

        const { accessToken, refreshToken } = data?.data;

        setAuthCookies(accessToken, refreshToken);

        api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        processQueue(null, accessToken);

        return api(originalRequest);
      }
      catch (refreshError) {
        console.error("Could not refresh token:", refreshError);
        processQueue(refreshError as Error, null);
        logoutUser();
        return Promise.reject(refreshError);
      }
      finally {
        isRefreshing = false;
      }
    }

    if (error.response?.data) {
      return Promise.reject(error.response.data);
    }

    return Promise.reject(
      new Error(
        JSON.stringify({
          message: error.message,
          errorType: "NETWORK_ERROR",
        }),
      ),
    );
  },
);

export default api;
